import { PermissionAccessProvider } from '../../../contexts/PermissionAccessContext';
import { newChildRow } from './ticketForm';
import { CHILD_FIELDS, STATE_OPTIONS, gridOptions } from './unitForm';
import { DateField, Section, SelectField, TextField, YesNoField } from './unitFields';
import { ChildListsProps, ChildrenStatus, UnitChildGrid } from './UnitChildGrid';
import { EndorsementsSection, FieldError, RestrictionGrids } from './certShared';

/**
 * Cert tab of the Unit drawer — V1 `@cert_layout`, two columns:
 * [License & Certs scalars; Permits grid] | [Endorsements (checkbox enables the expiry
 * date) + ISO tank / reefer approval; Restrictions grids per card type; Driver Corrective
 * Action (training) grid]. The scalars go to PUT /units/{r}/cert as a diff; the grids to
 * PUT /units/{r}/children with only the touched lists. Endorsements and the restriction
 * grids are the `certShared` pieces.
 */

export interface UnitCertTabProps extends ChildListsProps {
  cert: Record<string, any>;
  onCertChange: (key: string, value: unknown) => void;
  canEdit: boolean;
  portalContainer: HTMLElement | null;
  /** Scalar-field errors from the cert 422 (`field -> message`). */
  fieldErrors: Record<string, string>;
}

export default function UnitCertTab({
  cert, onCertChange, canEdit, portalContainer, fieldErrors,
  childRows, onChildRowsChange, lookups, childrenLoading, childrenError, rowErrors,
}: UnitCertTabProps) {
  const c = (key: string) => (v: unknown) => onCertChange(key, v);
  const gridsReady = !childrenLoading && !childrenError;

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* ---- Column 1: license & certs + permits ---- */}
        <div className="space-y-3">
          <Section title="License & Certs">
            <TextField label="License #" value={cert.driverlicnum} onChange={c('driverlicnum')} />
            <SelectField label="License State" value={cert.driverlicstate} onChange={c('driverlicstate')} options={STATE_OPTIONS} search />
            <DateField label="License Exp" value={cert.driverlicexpdate} onChange={c('driverlicexpdate')} portalContainer={portalContainer} />
            <FieldError message={fieldErrors.driverlicexpdate} />
            <DateField label="Hazmat Exp" value={cert.hazmatexpdate} onChange={c('hazmatexpdate')} portalContainer={portalContainer} />
            <DateField label="Med Card Date" value={cert.medcarddate} onChange={c('medcarddate')} portalContainer={portalContainer} />
            <DateField label="Med Card Exp" value={cert.medcardexpdate} onChange={c('medcardexpdate')} portalContainer={portalContainer} />
            <DateField label="TWIC Exp" value={cert.twicexpdate} onChange={c('twicexpdate')} portalContainer={portalContainer} />
            <DateField label="Annual Review Exp" value={cert.annualreviewexpdate} onChange={c('annualreviewexpdate')} portalContainer={portalContainer} />
            <DateField label="Clearinghouse Date" value={cert.clearinghousedate} onChange={c('clearinghousedate')} portalContainer={portalContainer} />
            <DateField label="Clearinghouse Exp" value={cert.clearinghouseexpdate} onChange={c('clearinghouseexpdate')} portalContainer={portalContainer} />
          </Section>
          {gridsReady ? (
            <UnitChildGrid
              title="Permits"
              rows={childRows.permits ?? []}
              onRowsChange={(rows) => onChildRowsChange('permits', rows)}
              columns={[
                { key: 'permittype', label: 'Permit Type', type: 'select', options: gridOptions(lookups.permittypes), className: 'min-w-[9rem]' },
                { key: 'permitdate', label: 'Permit Date', type: 'date' },
                { key: 'permitexpdate', label: 'Permit Exp', type: 'date' },
                { key: 'permitnum', label: 'Permit #', className: 'min-w-[6rem]' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.permits)}
              addLabel="Add permit"
              removeLabel="Remove permit"
              emptyText="No permits."
              errors={rowErrors.permits}
            />
          ) : <ChildrenStatus title="Permits" loading={childrenLoading} error={childrenError} />}
        </div>

        {/* ---- Column 2: endorsements + restrictions + training ---- */}
        <div className="space-y-3">
          <Section title="Endorsements">
            <EndorsementsSection values={cert} onChange={onCertChange} portalContainer={portalContainer} fieldErrors={fieldErrors} />
            <YesNoField label="ISO Tank Approved" value={cert.isotankapproved} onChange={c('isotankapproved')} />
            <YesNoField label="Reefer Approved" value={cert.reeferapproved} onChange={c('reeferapproved')} />
          </Section>

          {gridsReady ? (
            <RestrictionGrids
              rows={childRows.restrictions ?? []}
              onRowsChange={(rows) => onChildRowsChange('restrictions', rows)}
              restrictiontypes={lookups.restrictiontypes}
              newRow={(cardtype) => newChildRow(CHILD_FIELDS.restrictions, { cardtype })}
              errors={rowErrors.restrictions}
            />
          ) : <ChildrenStatus title="Restrictions" loading={childrenLoading} error={childrenError} />}

          {gridsReady ? (
            <UnitChildGrid
              title="Driver Corrective Action"
              rows={childRows.training ?? []}
              onRowsChange={(rows) => onChildRowsChange('training', rows)}
              columns={[
                { key: 'trainingtype', label: 'Training Type', type: 'select', options: gridOptions(lookups.trainingtypes), className: 'min-w-[10rem]' },
                { key: 'trainingsubjectrecno', label: 'Training Subject', type: 'select', options: gridOptions(lookups.trainingsubjects), className: 'min-w-[10rem]' },
                { key: 'trainingstartdate', label: 'Training Start', type: 'date' },
                { key: 'trainingenddate', label: 'Training End', type: 'date' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.training)}
              addLabel="Add corrective action"
              removeLabel="Remove corrective action"
              emptyText="No corrective actions."
              errors={rowErrors.training}
            />
          ) : <ChildrenStatus title="Driver Corrective Action" loading={childrenLoading} error={childrenError} />}
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
